import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { parseTradeCenterTab, withoutTabParam } from '../utils/tradeCenterTab';

/**
 * The Trade Center's active tab, honouring a one-shot `?tab=` instruction
 * (utils/tradeCenterTab.js, 2026-10-07).
 *
 * - At mount: a valid instruction wins over the remembered tab (savedTab,
 *   the one-shot "back from an offer" restore); otherwise the old behaviour:
 *   savedTab, else Received.
 * - While mounted: every navigation that carries a valid instruction (a new
 *   location.key, so tapping the same notification twice works) switches
 *   to that tab, even if the user had picked another one.
 * - The instruction is then removed from that history entry (replace, same
 *   state), so back/forward and reloads behave as before and nothing about
 *   the instruction is remembered.
 * - Missing or invalid values: no change at all, URL left untouched.
 *
 * `instructedAtMount` tells the page to skip restoring a remembered scroll
 * position, which belonged to whatever tab was remembered, not this one.
 */
export function useTradeCenterTab(savedTab) {
  const location = useLocation();
  const navigate = useNavigate();
  const [initial] = useState(() => parseTradeCenterTab(location.search));
  const instructedAtMount = useRef(initial != null).current;
  const [activeTab, setActiveTab] = useState(() => initial || savedTab || 'received');

  useEffect(() => {
    const tab = parseTradeCenterTab(location.search);
    if (!tab) return;
    setActiveTab(tab);
    navigate(
      { pathname: location.pathname, search: withoutTabParam(location.search), hash: location.hash },
      { replace: true, state: location.state }
    );
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  return { activeTab, setActiveTab, instructedAtMount };
}
